import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../utils/supabaseClient';

// Create auth context
const AuthContext = createContext();

// Get feature flag values from environment variables
const ENABLE_EMAIL_DOMAIN_RESTRICTION = process.env.REACT_APP_ENABLE_EMAIL_DOMAIN_RESTRICTION === 'true';
const ALLOWED_EMAIL_DOMAIN = process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN || 'density.io';

/**
 * Validate that the email is from the allowed domain
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether the email is valid
 */
const isValidDomain = (email) => {
  // If domain restriction is disabled, all emails are valid
  if (!ENABLE_EMAIL_DOMAIN_RESTRICTION) return true;
  
  // Otherwise, check if the email ends with the allowed domain
  return email.endsWith(`@${ALLOWED_EMAIL_DOMAIN}`);
};

/**
 * AuthProvider component to manage authentication state
 * @param {Object} props - Component props
 * @returns {JSX.Element} AuthProvider component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    // Check for active session using the current API
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setUser(data?.session?.user ?? null);
      setLoading(false);
    };
    
    checkSession();

    // Listen for auth changes using the current API
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`Auth event: ${event}`);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  /**
   * Sign up a new user
   * @param {Object} credentials - User credentials
   * @returns {Promise} - Auth response
   */
  const signUp = async ({ email, password }) => {
    try {
      setAuthError(null);
      
      // Validate email domain
      if (!isValidDomain(email)) {
        throw new Error(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed`);
      }
      
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password 
      });
      if (error) throw error;
      return { user: data.user, error: null };
    } catch (error) {
      console.error('Error signing up:', error.message);
      setAuthError(error.message);
      return { user: null, error };
    }
  };

  /**
   * Sign in an existing user
   * @param {Object} credentials - User credentials
   * @returns {Promise} - Auth response
   */
  const signIn = async ({ email, password }) => {
    try {
      setAuthError(null);
      
      // Validate email domain
      if (!isValidDomain(email)) {
        throw new Error(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed`);
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      if (error) throw error;
      return { user: data.user, error: null };
    } catch (error) {
      console.error('Error signing in:', error.message);
      setAuthError(error.message);
      return { user: null, error };
    }
  };

  /**
   * Sign out the current user
   * @returns {Promise} - Auth response
   */
  const signOut = async () => {
    try {
      setAuthError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error.message);
      setAuthError(error.message);
      return { error };
    }
  };

  /**
   * Reset password for a user
   * @param {string} email - User email
   * @returns {Promise} - Auth response
   */
  const resetPassword = async (email) => {
    try {
      setAuthError(null);
      
      // Validate email domain
      if (!isValidDomain(email)) {
        throw new Error(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed`);
      }
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error resetting password:', error.message);
      setAuthError(error.message);
      return { data: null, error };
    }
  };

  // Create context value
  const value = {
    user,
    loading,
    authError,
    signUp,
    signIn,
    signOut,
    resetPassword,
    domainRestriction: ENABLE_EMAIL_DOMAIN_RESTRICTION ? ALLOWED_EMAIL_DOMAIN : null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 * @returns {Object} Auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 